import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog } from "@/components/ui/Dialog";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { showToast } from "@/lib/toast";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import { nextOccurrence } from "@/lib/format";
import { UserMultiSelect } from "@/components/memo/UserMultiSelect";
import type { MeetingSeries, MemoUser, UUID } from "@/lib/types";

/** Today's date as a local YYYY-MM-DD (never the UTC day). */
function localToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const fieldLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--ink-2)",
  marginBottom: 4,
  display: "block",
};
const input: React.CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  boxSizing: "border-box",
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  series: MeetingSeries;
  sourceMemoId: UUID | null;
}

export function NewMemoDialog({ open, onOpenChange, series, sourceMemoId }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [date, setDate] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pending, setPending] = useState(false);
  const [people, setPeople] = useState<MemoUser[]>(series.participants);

  // Seed the form from the series cadence each time the dialog opens: the date
  // jumps to the next occurrence of the series' weekday (falling back to today
  // when there is no fixed day), the start time to the series' default, and the
  // attendance roster to the series participants (pre-checked).
  useEffect(() => {
    if (!open) return;
    const suggested = nextOccurrence(series.default_weekday ?? null, localToday());
    setDate(suggested ?? localToday());
    setStart(series.default_start_time ? series.default_start_time.slice(0, 5) : "");
    setEnd("");
    setPeople(series.participants);
  }, [open, series]);

  const canSave = !!date && !pending;

  const save = async () => {
    if (!canSave) return;
    setPending(true);
    const header = {
      meeting_date: date,
      start_time: start || null,
      end_time: end || null,
      participant_ids: people.map((u) => u.id),
    };
    try {
      const memo = sourceMemoId
        ? await api.copyForwardMemo(series.id, { ...header, source_memo_id: sourceMemoId })
        : await api.createMemo(series.id, header);
      void qc.invalidateQueries({ queryKey: queryKeys.seriesMemos(series.id) });
      onOpenChange(false);
      void navigate({ to: "/memo/$memoId", params: { memoId: memo.id } });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Fehler", "error");
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.seriesNewMemo}
      description={
        sourceMemoId
          ? "Offene und laufende Einträge werden übernommen; erledigte einmal als ✓ mit Lösung, abgeschlossene fallen weg."
          : undefined
      }
      width={480}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
        <div>
          <label style={fieldLabel}>{t.memoDateLabel}</label>
          <input
            type="date"
            style={input}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            autoFocus
          />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>{t.memoStartLabel}</label>
            <input
              type="time"
              style={input}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>{t.memoEndLabel}</label>
            <input type="time" style={input} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={fieldLabel}>{t.memoAttendanceLabel}</label>
          <UserMultiSelect value={people} onChange={setPeople} />
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          style={{
            alignSelf: "flex-end",
            background: BRAND.blue,
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "9px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: canSave ? "pointer" : "not-allowed",
            opacity: canSave ? 1 : 0.6,
          }}
        >
          {t.seriesNewMemo}
        </button>
      </div>
    </Dialog>
  );
}
